import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Input,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';

const BulkAssessments = () => {
  const [batchId, setBatchId] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setError('');
      } else {
        setError('Please select a CSV file');
        setFile(null);
        setFileName('');
      }
    }
  };

  const handleSubmit = async () => {
    if (!batchId.trim()) {
      setError('Please enter a batch ID');
      return;
    }

    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batch_id', batchId.trim());

      const token = localStorage.getItem('authToken'); // Fixed: use 'authToken' like other components
      const response = await fetch('https://sims.ethnotech.in/api/assessment/upload-csv', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data);
        setUploadProgress(100);
      } else {
        setError(data.detail || 'Assessment submission failed');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
      console.error('Assessment submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('authToken'); // Fixed: use 'authToken' like other components
      const response = await fetch('https://sims.ethnotech.in/api/assessment/csv-template', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'assessment_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to download template');
      }
    } catch (err) {
      setError(`Failed to download template: ${err.message}`);
    }
  };

  const downloadResults = () => {
    if (!results || !results.results) return;

    const csvContent = [
      ['Candidate ID', 'Status', 'Message', 'Success'].join(','),
      ...results.results.map(result => [
        result.candidateId || '',
        result.success ? 'Success' : 'Failed',
        (result.message || result.error || '').replace(/,/g, ';'),
        result.success ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessment_results_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusChip = (success) => {
    return success ? (
      <Chip
        icon={<CheckCircleIcon />}
        label="Success"
        color="success"
        size="small"
      />
    ) : (
      <Chip
        icon={<ErrorIcon />}
        label="Failed"
        color="error"
        size="small"
      />
    );
  };

  const resetForm = () => {
    setBatchId('');
    setFile(null);
    setFileName('');
    setResults(null);
    setError('');
    setUploadProgress(0);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Card sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        mb: 3
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Bulk Assessment Submission
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Submit assessment results for multiple candidates in a batch
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            Assessment Details
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              label="Batch ID"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              placeholder="Enter batch ID (e.g., 2735359)"
              variant="outlined"
              sx={{ minWidth: 200 }}
              disabled={loading}
            />
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              sx={{ minWidth: 200 }}
            >
              Download CSV Template
            </Button>
          </Box>

          <Box sx={{ mb: 3 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleFileChange}
              disabled={loading}
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={{ mr: 2, mb: 1 }}
                disabled={loading}
              >
                Select CSV File
              </Button>
            </label>
            {fileName && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                Selected: {fileName}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processing assessments...
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !batchId.trim() || !file}
              startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
              sx={{ minWidth: 150 }}
            >
              {loading ? 'Submitting...' : 'Submit Assessments'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={resetForm}
              disabled={loading}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      {results && (
        <Card sx={{ boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                Assessment Results
              </Typography>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={downloadResults}
                size="small"
              >
                Download Results
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {results.total || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Candidates
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {results.successful || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successful
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {results.failed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </Box>
            </Box>

            {results.results && results.results.length > 0 && (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Candidate ID</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Message</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.results.map((result, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{result.candidateId}</TableCell>
                        <TableCell>{getStatusChip(result.success)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {result.message || result.error || 'No message'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            CSV Format Requirements
          </Typography>
          <Typography variant="body2" paragraph>
            Your CSV file should include these columns:
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Required:</strong> candidateId, trainingStatus, attendance, assessmentStatus, 
            assessmentPercentage, grade, assessmentDataUploadedOn, assessmentAgency, 
            certificationName, isCertified, certifyingAgency, certificationDate
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Optional:</strong> assessorID, assessorName
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Date formats supported:</strong> YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, etc.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BulkAssessments;
